import { useMemo, useState } from "react";
import { allTrackers } from "app/services/track/track";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@similarweb/ui-components";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { MANAGEMENT_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import {
    SL_COMMON_CANCEL,
    SL_COMMON_SAVE,
    SL_LOAD_LIST_ASSIGN_NAME_HERE,
    SL_LOAD_LIST_ASSIGN_NAME_SUBTITLE,
    SL_LOAD_LIST_ASSIGN_NAME_TITLE,
    SL_LOAD_LIST_NAME_ALREADY_TAKEN_ERROR,
    SL_LOAD_LIST_NAME_FIELD_LABEL,
    SL_LOAD_LIST_NAME_FIELD_PLACEHOLDER,
    SL_LOAD_LIST_NAME_IS_TOO_LONG_ERROR,
} from "../../../translations/suppressionListKeys";
import {
    selectHasListOwnedByCurrentUser,
    selectParsedDomains,
    selectSuppressionLists,
    selectSuppressionListSendingError,
    selectTotalParsedRowsCount,
} from "../../../store/selectors/suppressionListSelectors";
import { resetParsedList, setCreateListStep } from "../../../store/actions/suppressionListActions";
import { sendParsedList } from "../../../store/effects/suppressionListApi";
import type { SuppressionList } from "../../types";
import { CreateSuppressionListStep } from "../../types";
import {
    StyledLoadList,
    StyledLoadListHeader,
    StyledLoadListSubtitle,
    StyledTextfield,
    StyledLoadListActionsContainer,
    StyledButton,
    StyledPreviousStepButton,
} from "../style";
import { hasSuccessfullyPostedSuppressionList } from "../../../store/action-utils";
import { translate } from "app/filters/i18nFilter";
import { MAX_NAME_WIDTH } from "../../constants";

export const AssignListNameView = ({ onCancel }: { onCancel?(): void }) => {
    const suppressionLists: SuppressionList[] = useSelector(selectSuppressionLists);
    const parsedDomains: string[] = useSelector(selectParsedDomains);
    const totalCount: number = useSelector(selectTotalParsedRowsCount);
    const isReplace: boolean = useSelector(selectHasListOwnedByCurrentUser);

    const domainsCount = parsedDomains.length;

    const defaultName = useMemo(() => suppressionLists.find((list) => list.isOwnedByRequestedUser)?.name ?? "", []);

    const sendingError: string = useSelector(selectSuppressionListSendingError);

    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState(defaultName);

    const dispatch = useDispatch();

    const format = (number: number): string => Intl.NumberFormat().format(number);

    const handleClickOnGoToPreviousStepButton = () => {
        dispatch(setCreateListStep(CreateSuppressionListStep.UPLOAD_FILE));
        dispatch(resetParsedList());
    };

    const handleClickOnSave = async () => {
        setIsSaving(true);
        const payload = { domains: parsedDomains, listName: name };
        const resultAction = await dispatch(sendParsedList(payload));

        if (hasSuccessfullyPostedSuppressionList(resultAction as any)) {
            proNavigator.go(MANAGEMENT_ROUTE);
            const action = isReplace ? "replace" : "upload";
            allTrackers.dispatchTrackEvent("suppression", `${action} list`, `account suppression/${action}/success`);
        }

        setIsSaving(false);
    };

    const isNameAlreadyTaken = suppressionLists.some((list) => !list.isOwnedByRequestedUser && list.name === name);
    const isEmpty = name.trim() === "";
    const isTooLong = name.length > MAX_NAME_WIDTH;

    const canSave = !isEmpty && !isNameAlreadyTaken && !isTooLong;

    const getErrorMessage = (): string => {
        if (isNameAlreadyTaken) {
            return translate(SL_LOAD_LIST_NAME_ALREADY_TAKEN_ERROR);
        }

        if (isTooLong) {
            return translate(SL_LOAD_LIST_NAME_IS_TOO_LONG_ERROR);
        }

        if (sendingError) {
            return sendingError;
        }

        return "";
    };

    const subtitleReplacements = {
        domainsCount: format(domainsCount),
        totalCount: format(totalCount),
    };

    const errorMessage = getErrorMessage();

    return (
        <StyledLoadList>
            <StyledLoadListHeader marginBottom="8px">{translate(SL_LOAD_LIST_ASSIGN_NAME_TITLE)}</StyledLoadListHeader>
            <StyledLoadListSubtitle>
                {translate(SL_LOAD_LIST_ASSIGN_NAME_SUBTITLE, subtitleReplacements)}
                <StyledPreviousStepButton
                    onClick={handleClickOnGoToPreviousStepButton}
                    disabled={isSaving}
                >
                    {translate(SL_LOAD_LIST_ASSIGN_NAME_HERE)}
                </StyledPreviousStepButton>
            </StyledLoadListSubtitle>
            <StyledTextfield
                label={translate(SL_LOAD_LIST_NAME_FIELD_LABEL)}
                placeholder={translate(SL_LOAD_LIST_NAME_FIELD_PLACEHOLDER)}
                onChange={setName}
                error={!!errorMessage}
                errorMessage={errorMessage}
                disabled={isSaving}
                defaultValue={defaultName}
            />
            <StyledLoadListActionsContainer>
                <StyledButton
                    type="flat"
                    onClick={onCancel}
                    isDisabled={isSaving}
                >
                    {translate(SL_COMMON_CANCEL)}
                </StyledButton>
                <Button
                    isDisabled={!canSave || isSaving}
                    onClick={handleClickOnSave}
                    isLoading={isSaving}
                >
                    {translate(SL_COMMON_SAVE)}
                </Button>
            </StyledLoadListActionsContainer>
        </StyledLoadList>
    );
};
